import { useEffect, useMemo, useRef, useState } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import { PERMISSIONS } from '@wms/shared';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { apiFetch, ApiError } from '../../lib/api';
import { useSession } from '../../lib/session';
import type { ProductListItem } from './types';
import { SkeletonRows } from '../skeleton';

const ROW_HEIGHT = 56;
const PAGE_SIZE = 100;

/**
 * 产品列表（S8）：虚拟化表格 —— 只渲染视口内的行，配合 M11 的性能目标
 * （P95 < 500ms）在数千 SKU 规模下仍然流畅。浏览与搜索两种模式服务端都
 * 返回游标（M4 独立复审整改：搜索不再静默截断），滚动接近底部时自动拉
 * 下一页——虚拟化 + 游标分页正是性能基线"从第一天就是默认做法"的组合。
 *
 * "全公司在手"列（S8 原始线框图的一列）刻意不渲染：M4 阶段没有任何真实
 * 库存数据，显示假的 0 或占位数字会让用户误以为这就是当前库存——诚实的
 * "—"用一个单独的空态提示替代整列，等 M5 有真实聚合后再补齐。
 *
 * 0023：批量归档——大批量导入基本不可逆（产品无批量物理删除），复选框 +
 * 顶部操作条把归档动作分组进一个可整批/逐个恢复的批次（archive-batches-page.tsx）。
 */
export function ProductListPage(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const canCreate = session?.permissions.includes(PERMISSIONS.PRODUCT_CREATE) ?? false;
  const canEdit = session?.permissions.includes(PERMISSIONS.PRODUCT_EDIT) ?? false;
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [note, setNote] = useState('');

  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['products', 'list', query],
    queryFn: ({ pageParam }) =>
      apiFetch<{ items: ProductListItem[]; nextCursor: string | null }>(
        `/products?limit=${PAGE_SIZE}${query ? `&query=${encodeURIComponent(query)}` : ''}${
          pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''
        }`,
      ),
    initialPageParam: '',
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

  const items = useMemo(() => (data?.pages ?? []).flatMap((p) => p.items), [data]);
  const activeItems = useMemo(() => items.filter((i) => i.status === 'ACTIVE'), [items]);
  const allVisibleSelected =
    activeItems.length > 0 && activeItems.every((i) => selected.has(i.id));

  const toggleOne = (id: string): void =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const toggleAllVisible = (): void =>
    setSelected((s) => {
      if (allVisibleSelected) {
        const next = new Set(s);
        for (const i of activeItems) next.delete(i.id);
        return next;
      }
      return new Set([...s, ...activeItems.map((i) => i.id)]);
    });

  const bulkArchive = useMutation({
    mutationFn: () =>
      apiFetch<{ archivedCount: number; skippedIds: string[] }>('/products/archive-batch', {
        method: 'POST',
        body: { productIds: [...selected], note: note.trim() || undefined },
      }),
    onSuccess: () => {
      setSelected(new Set());
      setNote('');
      setConfirmArchive(false);
      void queryClient.invalidateQueries({ queryKey: ['products', 'list'] });
    },
  });

  const parentRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index ?? -1;
  useEffect(() => {
    if (lastVisibleIndex >= items.length - 10 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [lastVisibleIndex, items.length, hasNextPage, isFetchingNextPage, fetchNextPage]);

  return (
    <div>
      <div className="row row--between">
        <h1 style={{ fontSize: 'var(--text-2xl)' }}>{t('nav.products')}</h1>
        <div className="row" style={{ gap: 'var(--space-2)' }}>
          {canEdit && (
            <Link to="/products/archive-batches" className="btn btn--ghost btn--sm">
              {t('products.archiveBatchesLink')}
            </Link>
          )}
          {canCreate && (
            <button className="btn btn--primary btn--sm" onClick={() => navigate('/products/new')}>
              {t('products.create')}
            </button>
          )}
        </div>
      </div>

      <input
        className="field"
        style={{ marginBottom: 'var(--space-4)' }}
        placeholder={t('products.searchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {canEdit && selected.size > 0 && (
        <div
          className="row row--between card"
          style={{ padding: 'var(--space-2) var(--space-3)', marginBottom: 'var(--space-3)' }}
        >
          <span>{t('products.selectedCount', { count: selected.size })}</span>
          <div className="row" style={{ gap: 'var(--space-2)' }}>
            <button className="btn btn--ghost btn--sm" onClick={() => setSelected(new Set())}>
              {t('products.clearSelection')}
            </button>
            <button className="btn btn--danger btn--sm" onClick={() => setConfirmArchive(true)}>
              {t('products.bulkArchiveAction')}
            </button>
          </div>
        </div>
      )}

      {isPending && <SkeletonRows />}
      {!isPending && items.length === 0 && (
        <div className="empty-state">
          <p>{query ? t('empty.noResults') : t('products.emptyState')}</p>
        </div>
      )}

      {items.length > 0 && (
        <div className="table" style={{ display: 'block' }}>
          <div
            className="row"
            style={{
              fontWeight: 600,
              padding: 'var(--space-2) 0',
              borderBottom: '1px solid var(--color-border)',
            }}
          >
            <span style={{ width: '48px' }}>
              {canEdit && activeItems.length > 0 && (
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  onChange={toggleAllVisible}
                  aria-label={t('products.selectAllVisible')}
                />
              )}
            </span>
            <span style={{ width: '48px' }} />
            <span style={{ flex: 2 }}>{t('products.colName')}</span>
            <span style={{ flex: 1 }}>{t('products.colSku')}</span>
            <span style={{ flex: 1 }}>{t('products.colBrand')}</span>
            <span style={{ flex: 1 }}>{t('products.colCategory')}</span>
            <span style={{ width: '80px' }}>{t('products.colBaseUnit')}</span>
            <span style={{ width: '100px' }}>{t('products.colStatus')}</span>
          </div>
          <div ref={parentRef} style={{ height: '600px', overflow: 'auto', position: 'relative' }}>
            <div style={{ height: `${virtualizer.getTotalSize()}px`, position: 'relative' }}>
              {virtualizer.getVirtualItems().map((row) => {
                const item = items[row.index]!;
                return (
                  <div
                    key={item.id}
                    className="tree__row"
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${row.size}px`,
                      transform: `translateY(${row.start}px)`,
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/products/${item.id}`)}
                  >
                    <span style={{ width: '48px' }} onClick={(e) => e.stopPropagation()}>
                      {canEdit && item.status === 'ACTIVE' && (
                        <input
                          type="checkbox"
                          checked={selected.has(item.id)}
                          onChange={() => toggleOne(item.id)}
                          aria-label={item.name}
                        />
                      )}
                    </span>
                    <span style={{ width: '48px' }}>
                      {item.primaryImageUrl ? (
                        <img
                          src={item.primaryImageUrl}
                          alt=""
                          style={{ width: 32, height: 32, objectFit: 'cover', borderRadius: 4 }}
                        />
                      ) : (
                        <span
                          style={{
                            display: 'inline-block',
                            width: 32,
                            height: 32,
                            borderRadius: 4,
                            background: 'var(--color-surface-sunken)',
                          }}
                        />
                      )}
                    </span>
                    <span style={{ flex: 2 }}>
                      {item.name}{' '}
                      {item.needsUnitSetup && (
                        <span className="badge badge--warn">{t('products.needsUnitSetupBadge')}</span>
                      )}{' '}
                      {item.importPendingReview && (
                        <span className="badge badge--info">{t('products.pendingReviewBadge')}</span>
                      )}
                    </span>
                    <span style={{ flex: 1 }}>{item.sku}</span>
                    <span style={{ flex: 1 }}>{item.brandName ?? '—'}</span>
                    <span style={{ flex: 1 }}>{item.categoryName ?? '—'}</span>
                    <span style={{ width: '80px' }}>{item.baseUnitCode}</span>
                    <span style={{ width: '100px' }}>
                      {item.status === 'ARCHIVED' ? (
                        <span className="badge badge--muted">{t('status.archived')}</span>
                      ) : (
                        <span className="badge badge--success">{t('products.statusActive')}</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {confirmArchive && (
        <div className="modal-overlay" role="presentation" onClick={() => setConfirmArchive(false)}>
          <div className="modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <h2>{t('products.bulkArchiveConfirmTitle')}</h2>
            <p>{t('products.bulkArchiveConfirmBody', { count: selected.size })}</p>
            <label className="field">
              <span>{t('products.bulkArchiveNoteLabel')}</span>
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={2000} />
            </label>
            {bulkArchive.error instanceof ApiError && (
              <p className="field-error">
                {t(`errors:${bulkArchive.error.code}`, {
                  defaultValue: bulkArchive.error.code,
                  ...bulkArchive.error.params,
                })}
              </p>
            )}
            <div className="modal__actions">
              <button
                type="button"
                className="btn btn--ghost"
                disabled={bulkArchive.isPending}
                onClick={() => setConfirmArchive(false)}
              >
                {t('app.cancel')}
              </button>
              <button
                type="button"
                className="btn btn--danger"
                disabled={bulkArchive.isPending}
                onClick={() => bulkArchive.mutate()}
              >
                {bulkArchive.isPending ? t('app.loading') : t('products.bulkArchiveConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
